import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { campaignKey, DEFINITION, definitionMeta, newDefinition, type Defined } from './definition';
import { createDefineWrite } from './defineWrite';
import { toListRead, toRead, type CurrentRead, type ListRead, type Read } from './read';
import { readsMeta, type ReadEndpoint } from './reads';

/** Pass instead of params when the query should not run: its read is `idle`. */
export const skip: unique symbol = Symbol('skip');

const PARENT: unique symbol = Symbol('parent');

/** Params that arrive only when a parent read is ready. */
export interface Dependent<P> {
  readonly [PARENT]: Read<P>;
}

/**
 * Run the query with the parent's value once the parent is ready. Until then the child is
 * `idle` waiting on its parent, and a failed or offline parent makes the child failed or offline,
 * so "disabled until the parent is ready" is never a hand-written `enabled` flag.
 */
export function whenReady<P>(parent: Read<P>): Dependent<P> {
  return { [PARENT]: parent };
}

export type QueryInput<P> = P | Dependent<P> | typeof skip;

function isDependent<P>(input: QueryInput<P>): input is Dependent<P> {
  return typeof input === 'object' && input !== null && PARENT in input;
}

type Resolved<P> = { run: true; params: P } | { run: false; read: Read<never> };

/** The params to run with, or the read the query stands at without running. */
export function resolveInput<P>(input: QueryInput<P>): Resolved<P> {
  if (input === skip) return { run: false, read: { status: 'idle', waitingOn: 'not-requested' } };
  if (!isDependent(input)) return { run: true, params: input };
  const parent = input[PARENT];
  switch (parent.status) {
    case 'ready':
      return { run: true, params: parent.data };
    case 'failed':
    case 'offline':
      return { run: false, read: parent };
    case 'idle':
    case 'loading':
    case 'placeholder':
      return { run: false, read: { status: 'idle', waitingOn: 'parent' } };
  }
}

/**
 * What each platform supplies. The package never imports app code: web reads its campaign
 * store and the browser's online state, mobile its store and NetInfo.
 */
export interface DataLayerConfig {
  useCampaignId: () => string | null | undefined;
  useOnline: () => boolean;
  /**
   * Every query asks for one campaign's data, so until a campaign is selected each read is
   * `idle` instead of a request the server refuses for want of `X-Campaign-Id`.
   */
  requireCampaign?: boolean;
}

/**
 * What a fetch is handed. `campaignId` is the id the layer put at the head of the cache key; the
 * fetch must send it as the request's `X-Campaign-Id` rather than read the campaign again, or a
 * campaign switch mid-flight caches one campaign's answer under the other's key.
 */
export interface FetchContext {
  signal: AbortSignal;
  campaignId: string | null;
}

interface QueryDefinition<P, R> {
  /** The key after the campaign id, which the layer prepends to every key. */
  key: (params: P) => readonly unknown[];
  /** Must request `context.campaignId`'s data (see {@link FetchContext}). */
  fetch: (params: P, context: FetchContext) => Promise<R>;
  /** The declared GET this fetches, so a write naming one of its entity types refreshes it. */
  reads?: ReadEndpoint;
  staleTime?: number;
  /**
   * Refetch this often (ms) while observed, for a value the server can change with no write from
   * this client. A function is asked with the params on every tick; `false` stops polling.
   */
  refetchInterval?: Interval<P>;
  /** `false` for a read too costly to refetch on every tab switch. */
  refetchOnWindowFocus?: false;
}

type Interval<P> = number | ((params: P) => number | false);

function intervalFor<P>(interval: Interval<P> | undefined, resolved: Resolved<P>): number | (() => number | false) | undefined {
  if (typeof interval !== 'function') return interval;
  return () => (resolved.run ? interval(resolved.params) : false);
}

interface InfiniteQueryDefinition<P, Page, Item, Cursor> {
  key: (params: P) => readonly unknown[];
  /** Must request `context.campaignId`'s data (see {@link FetchContext}). */
  fetch: (params: P, cursor: Cursor, context: FetchContext) => Promise<Page>;
  initialCursor: Cursor;
  /** The cursor for the page after `last`, or `undefined` when there is none. */
  nextCursor: (last: Page, pages: Page[]) => Cursor | undefined;
  items: (page: Page) => readonly Item[];
  /** The declared GET this fetches, so a write naming one of its entity types refreshes it. */
  reads?: ReadEndpoint;
  staleTime?: number;
  refetchInterval?: Interval<P>;
  refetchOnWindowFocus?: false;
}

/**
 * An infinite read. With `keepPrevious` a params change keeps the previous params' rows on
 * screen as `placeholder` until the new first page lands, so a filter keystroke does not unmount
 * the list; `placeholder` rather than `ready`, so nothing counts or files from rows that answer
 * another filter. `hasMore` is false while it is.
 */
export interface InfiniteRead<Item, Page = unknown> {
  read: ListRead<Item>;
  /** The first page as the server sent it, for the envelope around the rows; there whenever rows are. */
  first: Page | undefined;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

/** An infinite read that did not opt into `keepPrevious`: it never shows a previous key's rows. */
export interface CurrentInfiniteRead<Item, Page = unknown> extends InfiniteRead<Item, Page> {
  read: Exclude<ListRead<Item>, { status: 'placeholder' }>;
}

export function createDataLayer(config: DataLayerConfig) {
  function resolveLayerInput<P>(input: QueryInput<P>, campaignId: string | null): Resolved<P> {
    if (config.requireCampaign && campaignId === null) return resolveInput<P>(skip);
    return resolveInput(input);
  }

  function defineQuery<P, R>(def: QueryDefinition<P, R> & { keepPrevious: true }): ((input: QueryInput<P>) => Read<R>) & Defined<P, R>;
  function defineQuery<P, R>(def: QueryDefinition<P, R> & { keepPrevious?: false }): ((input: QueryInput<P>) => CurrentRead<R>) & Defined<P, R>;
  function defineQuery<P, R>(def: QueryDefinition<P, R> & { keepPrevious?: boolean }): ((input: QueryInput<P>) => Read<R>) & Defined<P, R> {
    const definition = newDefinition(def.key);
    const meta = definitionMeta(definition, def.reads && readsMeta(def.reads));
    function useDefinedQuery(input: QueryInput<P>): Read<R> {
      const campaignId = config.useCampaignId() ?? null;
      const online = config.useOnline();
      const resolved = resolveLayerInput(input, campaignId);
      const query = useQuery({
        queryKey: campaignKey(campaignId, resolved.run ? def.key(resolved.params) : ['@signalvote/data:not-run', definition.id]),
        queryFn: ({ signal }) => def.fetch((resolved as { params: P }).params, { signal, campaignId }),
        enabled: resolved.run,
        placeholderData: def.keepPrevious ? keepPreviousData : undefined,
        staleTime: def.staleTime,
        refetchInterval: intervalFor(def.refetchInterval, resolved),
        refetchOnWindowFocus: def.refetchOnWindowFocus,
        meta,
      });
      if (!resolved.run) return resolved.read;
      return toRead(query, query.data, { online });
    }
    return Object.assign(useDefinedQuery, { [DEFINITION]: definition });
  }

  type InfiniteHook<P, Page, Cursor, Rd> = ((input: QueryInput<P>) => Rd) & Defined<P, InfiniteData<Page, Cursor>>;
  function defineInfiniteQuery<P, Page, Item, Cursor>(
    def: InfiniteQueryDefinition<P, Page, Item, Cursor> & { keepPrevious: true },
  ): InfiniteHook<P, Page, Cursor, InfiniteRead<Item, Page>>;
  function defineInfiniteQuery<P, Page, Item, Cursor>(
    def: InfiniteQueryDefinition<P, Page, Item, Cursor> & { keepPrevious?: false },
  ): InfiniteHook<P, Page, Cursor, CurrentInfiniteRead<Item, Page>>;
  function defineInfiniteQuery<P, Page, Item, Cursor>(
    def: InfiniteQueryDefinition<P, Page, Item, Cursor> & { keepPrevious?: boolean },
  ): InfiniteHook<P, Page, Cursor, InfiniteRead<Item, Page>> {
    const definition = newDefinition(def.key);
    const meta = definitionMeta(definition, def.reads && readsMeta(def.reads));
    function useDefinedInfiniteQuery(input: QueryInput<P>): InfiniteRead<Item, Page> {
      const campaignId = config.useCampaignId() ?? null;
      const online = config.useOnline();
      const resolved = resolveLayerInput(input, campaignId);
      const query = useInfiniteQuery({
        queryKey: campaignKey(campaignId, resolved.run ? def.key(resolved.params) : ['@signalvote/data:not-run', definition.id]),
        queryFn: ({ pageParam, signal }) =>
          def.fetch((resolved as { params: P }).params, pageParam as Cursor, { signal, campaignId }),
        initialPageParam: def.initialCursor,
        getNextPageParam: def.nextCursor,
        enabled: resolved.run,
        placeholderData: def.keepPrevious ? keepPreviousData : undefined,
        staleTime: def.staleTime,
        refetchInterval: intervalFor(def.refetchInterval, resolved),
        refetchOnWindowFocus: def.refetchOnWindowFocus,
        meta,
      });
      const pages = query.data?.pages;
      const items = useMemo(() => pages?.flatMap((page) => [...def.items(page)]), [pages]);
      const read: ListRead<Item> = resolved.run ? toListRead(query, items, { online }) : resolved.read;
      const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
      const loadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
      }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
      return {
        read,
        first: 'data' in read ? query.data?.pages[0] : undefined,
        hasMore: resolved.run && hasNextPage,
        loadingMore: isFetchingNextPage,
        loadMore,
      };
    }
    return Object.assign(useDefinedInfiniteQuery, { [DEFINITION]: definition });
  }

  return { defineQuery, defineInfiniteQuery, defineWrite: createDefineWrite(config) };
}
